import { useMutation, useQueryClient } from '@tanstack/react-query'

import { authAPI } from '@/api/new-api'
import { HomepageConfigResponse } from '@/pages/building-dashboard/home-screen/home-screen.types'

import { getHomepageConfigQueryKey } from './get-homepage-config'

const updateHomepageConfig = async (data: HomepageConfigResponse) => {
  const response = await authAPI.post<HomepageConfigResponse>('/homepage', data)
  return response.data
}

type UpdateHomepageConfigContext = {
  previous: HomepageConfigResponse | undefined
  queryKey: ReturnType<typeof getHomepageConfigQueryKey>
}

// Optimistic update + rollback: mirrors the legacy page's "update local state immediately,
// re-fetch to revert on error" UX, but via the React Query cache instead of local state.
export const useUpdateHomepageConfig = () => {
  const queryClient = useQueryClient()

  return useMutation<
    HomepageConfigResponse,
    unknown,
    HomepageConfigResponse,
    UpdateHomepageConfigContext
  >({
    mutationFn: updateHomepageConfig,
    onError: (_error, _variables, context) => {
      if (!context) return
      queryClient.setQueryData(context.queryKey, context.previous)
    },
    onMutate: async (newConfig) => {
      const queryKey = getHomepageConfigQueryKey({ buildingId: newConfig.buildingId })
      await queryClient.cancelQueries({ queryKey })
      const previous = queryClient.getQueryData<HomepageConfigResponse>(queryKey)
      queryClient.setQueryData(queryKey, newConfig)
      return { previous, queryKey }
    },
    onSettled: (_data, _error, variables) => {
      queryClient.invalidateQueries({
        queryKey: getHomepageConfigQueryKey({ buildingId: variables.buildingId }),
      })
    },
  })
}
